import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  PeersonaChatBotMeta,
  PeersonaChunkInfo,
  PeersonaHandoffInfo,
  PeersonaMessageRow,
  PeersonaPeerLookupInfo,
  PeersonaSafetyResource,
  PeersonaStreamEvent,
  PeersonaThreadSummary,
  getPeersonaThread,
  peersonaKeys,
  postPeersonaEnd,
  streamPeersonaChat,
  usePeersonaChatBot,
  usePeersonaThreads,
} from '../hooks/api/usePeersonaChat';
import { useAssistantWidget } from './assistant-widget-context';
import useAuth from '../hooks/utils/useAuth';
import { useSessionStorage } from '../hooks/utils/useSessionStorage';

// Per-peer pointer to the thread the user was last in, so a page reload
// resumes the same conversation instead of dropping it. Keyed by chatbot id
// because a user can hold separate threads with different Peersonas.
const lastThreadKey = (chatbotId: number) => `peersona:lastThread:${chatbotId}`;
const LAST_THREAD_KEY_PREFIX = 'peersona:lastThread:';

/** Fresh-login flag from `auth-context`; consuming it scrubs per-peer last-thread pointers so the next open starts blank. */
const PEERSONA_FRESH_SESSION_KEY = 'kh:peersona:freshSession';
const consumePeersonaFreshSessionFlag = (): boolean => {
  try {
    if (sessionStorage.getItem(PEERSONA_FRESH_SESSION_KEY) !== '1') return false;
    sessionStorage.removeItem(PEERSONA_FRESH_SESSION_KEY);
    return true;
  } catch {
    return false;
  }
};

const scrubAllPeersonaLastThreadKeys = (): void => {
  try {
    // Iterate in reverse — removeItem shifts indices, and we don't want to
    // skip keys mid-loop.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LAST_THREAD_KEY_PREFIX)) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* storage disabled — nothing to scrub */
  }
};

const readLastThreadId = (chatbotId: number): number | null => {
  try {
    const raw = localStorage.getItem(lastThreadKey(chatbotId));
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

const writeLastThreadId = (chatbotId: number, threadId: number | null): void => {
  try {
    if (threadId == null) localStorage.removeItem(lastThreadKey(chatbotId));
    else localStorage.setItem(lastThreadKey(chatbotId), String(threadId));
  } catch {
    /* storage disabled — restore simply won't happen */
  }
};

// Map persisted history rows into display messages. Rows are NOT marked
// ``streaming`` so re-hydrated handoffs render their manual CTA immediately.
const mapRowsToDisplay = (rows: PeersonaMessageRow[]): PeersonaDisplayMessage[] =>
  (rows ?? []).map((r) => ({ ...r, streaming: false }));

export type PeersonaDisplayMessage = PeersonaMessageRow & {
  streaming?: boolean;
};

export interface PeersonaStage {
  stage: string;
  message: string;
  top_score?: number;
}

interface PeersonaContextValue {
  chatbotId: number | null;
  chatbot: PeersonaChatBotMeta | null;
  panelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  setChatbotId: (id: number | null) => void;
  /**
   * Convenience for "set chatbot + open widget" in a single atomic call.
   * Prefer this over ``setChatbotId(id); openPanel();`` from tiles / overlays
   * — the coordinator needs the chatbot id at the moment ``openWidget`` is
   * invoked, and the two-call pattern would otherwise race the state batch.
   */
  openPanelForChatbot: (id: number) => void;
  /** Deep-link variant of ``openPanelForChatbot`` that resumes a specific past thread. */
  openPanelForChatbotThread: (chatbotId: number, threadId: number) => void;
  messages: PeersonaDisplayMessage[];
  threadId: number | null;
  isSending: boolean;
  stage: PeersonaStage | null;
  lastTier: string | null;
  lastChunks: PeersonaChunkInfo[];
  /** The current user's recent threads with this Peersona (newest first). */
  recentThreads: PeersonaThreadSummary[];
  /** True while a past thread's transcript is being hydrated. */
  isLoadingThread: boolean;
  sendMessage: (text: string) => Promise<void>;
  /** Clear the panel and start a brand-new thread (old thread stays in history). */
  startNewChat: () => void;
  /** Open a past thread from the recent-chats list. */
  loadThread: (threadId: number) => Promise<void>;
  resetSession: () => Promise<void>;
  cancelStream: () => void;
}

const PeersonaContext = createContext<PeersonaContextValue | null>(null);

export const usePeersona = (): PeersonaContextValue => {
  const ctx = useContext(PeersonaContext);
  if (!ctx) throw new Error('usePeersona must be used within PeersonaProvider');
  return ctx;
};

interface ProviderProps {
  initialChatbotId?: number | null;
  children: React.ReactNode;
}

export const PeersonaProvider: React.FC<ProviderProps> = ({ initialChatbotId = null, children }) => {
  const [chatbotId, setChatbotIdState] = useState<number | null>(initialChatbotId);

  // Visibility lives on the AssistantWidgetContext coordinator. A Peersona
  // panel is "open" only when the widget is open AND the active context is
  // this peer's chatbot id. That lets users tab away to Fi without losing
  // the Peersona thread.
  const { widgetOpen, activeContext, openWidget, closeWidget, switchContext } = useAssistantWidget();
  const panelOpen =
    widgetOpen && activeContext.kind === 'peersona' && chatbotId != null && activeContext.chatbotId === chatbotId;

  const [messages, setMessages] = useState<PeersonaDisplayMessage[]>([]);
  const [threadId, setThreadId] = useState<number | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [stage, setStage] = useState<PeersonaStage | null>(null);
  const [lastTier, setLastTier] = useState<string | null>(null);
  const [lastChunks, setLastChunks] = useState<PeersonaChunkInfo[]>([]);
  const [isLoadingThread, setIsLoadingThread] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const sendGenerationRef = useRef(0);
  // Bumped whenever we kick off a restore/load so a slow fetch that resolves
  // after the user switched peers or started a new chat is discarded.
  const loadGenerationRef = useRef(0);

  const queryClient = useQueryClient();
  // Gate on auth — the provider mounts above the routes, so without this
  // the demo chatbot env-var would 403 anonymously on public pages and
  // trip the global 403-redirect handler in ``index.tsx`` into a loop.
  const { authToken } = useAuth();
  // Re-runs when ``markFreshAuthSession`` writes the flag (via the custom
  // sessionStorageChange event), so we still scrub even if the flag is set
  // in the same tick as ``setAuthToken`` but after an earlier authToken effect.
  const peersonaFreshSessionFlag = useSessionStorage(PEERSONA_FRESH_SESSION_KEY);
  const isAuthenticated = Boolean(authToken);
  const { data: chatbot } = usePeersonaChatBot(chatbotId, isAuthenticated && chatbotId != null);
  const { data: recentThreads = [] } = usePeersonaThreads(chatbotId, isAuthenticated && chatbotId != null);

  // When the widget coordinator restores a Peersona peer after reload (or the
  // user switches peers via the unified widget), align local chatbotId so
  // ``panelOpen`` and thread hydration target the same peer.
  useEffect(() => {
    if (activeContext.kind !== 'peersona') return;
    if (activeContext.chatbotId === chatbotId) return;
    setChatbotIdState(activeContext.chatbotId);
  }, [activeContext, chatbotId]);

  const openPanel = useCallback(() => {
    if (chatbotId == null) return;
    openWidget({ kind: 'peersona', chatbotId });
  }, [chatbotId, openWidget]);

  const closePanel = useCallback(() => closeWidget(), [closeWidget]);

  const togglePanel = useCallback(() => {
    if (panelOpen) {
      closeWidget();
    } else if (chatbotId != null) {
      openWidget({ kind: 'peersona', chatbotId });
    }
  }, [panelOpen, chatbotId, openWidget, closeWidget]);

  const openPanelForChatbot = useCallback(
    (id: number) => {
      setChatbotIdState(id);
      openWidget({ kind: 'peersona', chatbotId: id });
    },
    [openWidget],
  );

  // If we're already on this chatbot, the chatbotId effect won't re-fire,
  // so hydrate the thread manually (deferred via setTimeout to dodge TDZ
  // on ``hydrateThread``). Otherwise let the chatbotId effect pick up the
  // ``lastThread`` value we just wrote.
  const openPanelForChatbotThread = useCallback(
    (id: number, tid: number) => {
      writeLastThreadId(id, tid);
      if (chatbotId === id) {
        setTimeout(() => {
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          void hydrateThread(tid, true);
        }, 0);
      } else {
        setChatbotIdState(id);
      }
      openWidget({ kind: 'peersona', chatbotId: id });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chatbotId, openWidget],
  );

  // Wrap setChatbotId so that swapping peers while the widget is currently
  // showing this peer's panel automatically retargets the active context.
  // Without this, switching peers mid-session would orphan the open panel
  // (panelOpen would become false because activeContext.chatbotId no longer
  // matches the new chatbotId).
  const setChatbotId = useCallback(
    (id: number | null) => {
      setChatbotIdState(id);
      if (id != null && activeContext.kind === 'peersona' && widgetOpen) {
        switchContext({ kind: 'peersona', chatbotId: id });
      }
    },
    [activeContext, widgetOpen, switchContext],
  );

  const cancelStream = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  // When the panel closes mid-stream, cancel; we'll start over on next send.
  useEffect(() => {
    if (!panelOpen) cancelStream();
  }, [panelOpen, cancelStream]);

  const clearThreadState = useCallback(() => {
    setMessages([]);
    setThreadId(null);
    setStage(null);
    setLastTier(null);
    setLastChunks([]);
  }, []);

  // Hydrate an existing thread's transcript into the panel. Used both for
  // restore-on-reload and for opening a past chat from the recent list. A
  // generation token guards against a slow fetch resolving after the user
  // has already switched peers or started a new chat.
  const hydrateThread = useCallback(
    async (id: number, showSpinner: boolean) => {
      if (chatbotId == null) return;
      cancelStream();
      const myGen = ++loadGenerationRef.current;
      // Invalidate any in-flight stream callbacks too.
      sendGenerationRef.current++;
      if (showSpinner) setIsLoadingThread(true);
      try {
        const detail = await getPeersonaThread(chatbotId, id);
        if (myGen !== loadGenerationRef.current) return;
        const rows = mapRowsToDisplay(detail.message_history ?? []);
        setMessages(rows);
        setThreadId(detail.thread_id);
        writeLastThreadId(chatbotId, detail.thread_id);
        setStage(null);
        const lastAssistant = [...rows].reverse().find((m) => m.role === 'assistant');
        setLastTier(lastAssistant?.peersona?.tier ?? null);
        setLastChunks(lastAssistant?.peersona?.chunks ?? []);
      } catch (err) {
        if (myGen !== loadGenerationRef.current) return;
        // Stored pointer is stale (deleted / different user) — drop it and
        // fall back to a clean slate so the panel still works.
        // eslint-disable-next-line no-console
        console.warn('[peersona] failed to load thread', err);
        writeLastThreadId(chatbotId, null);
        clearThreadState();
      } finally {
        if (myGen === loadGenerationRef.current && showSpinner) setIsLoadingThread(false);
      }
    },
    [cancelStream, chatbotId, clearThreadState],
  );

  const loadThread = useCallback(
    async (id: number) => {
      await hydrateThread(id, true);
    },
    [hydrateThread],
  );

  const startNewChat = useCallback(() => {
    cancelStream();
    loadGenerationRef.current++;
    sendGenerationRef.current++;
    if (chatbotId != null) writeLastThreadId(chatbotId, null);
    clearThreadState();
  }, [cancelStream, chatbotId, clearThreadState]);

  // Runs BEFORE the chatbotId resume effect below so the stored pointer is
  // wiped before `readLastThreadId` would see it.
  useEffect(() => {
    if (!authToken || peersonaFreshSessionFlag !== '1') return;
    if (!consumePeersonaFreshSessionFlag()) return;
    scrubAllPeersonaLastThreadKeys();
    // Reset in-memory state too — covers the case where the panel is already
    // mounted with stale messages from the previous user/session.
    cancelStream();
    loadGenerationRef.current++;
    sendGenerationRef.current++;
    setMessages([]);
    setThreadId(null);
    setStage(null);
    setLastTier(null);
    setLastChunks([]);
  }, [authToken, peersonaFreshSessionFlag, cancelStream]);

  // On mount / peer switch, resume the user's last thread for this peer if we
  // have one stored; otherwise start clean. Replaces the old unconditional
  // reset so a page reload no longer drops the conversation.
  useEffect(() => {
    cancelStream();
    setStage(null);
    if (chatbotId == null) {
      loadGenerationRef.current++;
      clearThreadState();
      return;
    }
    const stored = readLastThreadId(chatbotId);
    if (stored != null) {
      void hydrateThread(stored, true);
    } else {
      loadGenerationRef.current++;
      clearThreadState();
    }
    // hydrateThread is stable per chatbotId; clearThreadState/cancelStream are stable.
  }, [chatbotId, cancelStream, clearThreadState, hydrateThread]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || chatbotId == null) return;

      cancelStream();
      const myGen = ++sendGenerationRef.current;
      const controller = new AbortController();
      abortRef.current = controller;

      const userTurn: PeersonaDisplayMessage = {
        role: 'user',
        content: trimmed,
        timestamp: new Date().toISOString(),
      };
      const assistantTurn: PeersonaDisplayMessage = {
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        streaming: true,
      };
      setMessages((prev) => [...prev, userTurn, assistantTurn]);
      setIsSending(true);
      setStage({ stage: 'sending', message: 'Sending message…' });

      let collectedTokens = '';
      let collectedSafetyResources: PeersonaSafetyResource[] = [];
      // Stash the handoff / peer_lookup payloads as they stream in so the
      // 'done' handler can fold them into the canonical message metadata.
      // For handoff specifically, an early ``handoff`` event lets us flip
      // the bubble UI before generation finishes — the concierge ack is
      // templated, so the model isn't actually streaming tokens for these
      // turns, but we keep the same pattern so the contract stays uniform.
      let collectedHandoff: PeersonaHandoffInfo | null = null;
      let collectedPeerLookup: PeersonaPeerLookupInfo | null = null;

      const updateAssistantTail = (mutator: (m: PeersonaDisplayMessage) => PeersonaDisplayMessage) => {
        setMessages((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i--) {
            if (next[i]?.role === 'assistant') {
              next[i] = mutator(next[i]);
              break;
            }
          }
          return next;
        });
      };

      try {
        await streamPeersonaChat({
          chatbotId,
          message: trimmed,
          threadId,
          signal: controller.signal,
          onEvent: (event: PeersonaStreamEvent) => {
            if (myGen !== sendGenerationRef.current) return;
            switch (event.type) {
              case 'stage':
                setStage({ stage: event.stage, message: event.message, top_score: event.top_score });
                if (event.stage.startsWith('tier_')) {
                  setLastTier(event.stage.replace('tier_', ''));
                }
                break;
              case 'token':
                collectedTokens += event.text;
                updateAssistantTail((m) => ({ ...m, content: collectedTokens, streaming: true }));
                break;
              case 'crisis':
                // The orchestrator emits this BEFORE the reply streams so the
                // UI knows it's a crisis-flagged turn. We don't render a
                // standalone crisis indicator — the safety_resources event
                // (which follows on the same turn) is the visible signal.
                if (event.crisis_detected) {
                  // eslint-disable-next-line no-console
                  console.warn('[peersona] crisis signal', event);
                }
                break;
              case 'safety_resources':
                // Render the safety card visually attached beneath the
                // (still-streaming) bubble. Threading into the message
                // before tokens finish means the card appears in sync
                // with the model's reply referencing "the info below".
                collectedSafetyResources = event.resources ?? [];
                updateAssistantTail((m) => ({
                  ...m,
                  peersona: {
                    ...(m.peersona ?? {}),
                    safety_resources: collectedSafetyResources,
                  },
                }));
                break;
              case 'handoff': {
                // Strip the SSE-event discriminator before persisting; the
                // remaining fields ARE the PeersonaHandoffInfo shape.
                const { type: _eventType, ...handoffPayload } = event;
                collectedHandoff = handoffPayload as PeersonaHandoffInfo;
                updateAssistantTail((m) => ({
                  ...m,
                  peersona: {
                    ...(m.peersona ?? {}),
                    handoff: collectedHandoff!,
                  },
                }));
                break;
              }
              case 'done':
                setThreadId(event.thread_id);
                if (chatbotId != null) {
                  writeLastThreadId(chatbotId, event.thread_id);
                  // Refresh the recent-chats list so a brand-new thread (or
                  // an updated preview/title) shows up without a manual reload.
                  void queryClient.invalidateQueries({ queryKey: peersonaKeys.threads(chatbotId) });
                }
                setLastTier(event.tier);
                setLastChunks(event.chunks ?? []);
                if (event.handoff) collectedHandoff = event.handoff;
                if (event.peer_lookup) collectedPeerLookup = event.peer_lookup;
                updateAssistantTail((m) => ({
                  ...m,
                  content: event.ai_response || m.content,
                  streaming: false,
                  peersona: {
                    tier: event.tier,
                    top_score: event.top_score,
                    chunks: event.chunks,
                    intent: event.intent,
                    ...(event.referral ? { referral: event.referral } : {}),
                    ...(collectedHandoff ? { handoff: collectedHandoff } : {}),
                    ...(collectedPeerLookup ? { peer_lookup: collectedPeerLookup } : {}),
                    // Prefer the done payload's safety_resources (canonical),
                    // fall back to what the safety_resources event delivered
                    // mid-stream so we never lose them.
                    ...(event.safety_resources?.length
                      ? { safety_resources: event.safety_resources }
                      : collectedSafetyResources.length
                        ? { safety_resources: collectedSafetyResources }
                        : {}),
                  },
                }));
                break;
              case 'error':
                updateAssistantTail((m) => ({
                  ...m,
                  content: m.content || event.message || 'Something went wrong while replying.',
                  streaming: false,
                }));
                break;
            }
          },
        });
      } catch (err: unknown) {
        if (myGen !== sendGenerationRef.current) return;
        if (err instanceof DOMException && err.name === 'AbortError') {
          updateAssistantTail((m) => ({ ...m, streaming: false }));
        } else {
          // eslint-disable-next-line no-console
          console.error('[peersona] stream error', err);
          const detail = err instanceof Error && err.message ? err.message : '';
          updateAssistantTail((m) => ({
            ...m,
            content:
              collectedTokens ||
              (detail ? `Couldn't reply: ${detail}` : "I'm having trouble responding right now. Please try again."),
            streaming: false,
          }));
        }
      } finally {
        if (myGen === sendGenerationRef.current) {
          setIsSending(false);
          setStage(null);
          if (abortRef.current === controller) abortRef.current = null;
        }
      }
    },
    [cancelStream, chatbotId, threadId, queryClient],
  );

  const resetSession = useCallback(async () => {
    // Ends the server thread via postPeersonaEnd. Not exposed in PeersonaHeader yet —
    // header only offers startNewChat (local clear). Deferred KINDLY-956.
    // https://listenersoncall.atlassian.net/browse/KINDLY-956
    cancelStream();
    loadGenerationRef.current++;
    sendGenerationRef.current++;
    if (chatbotId != null && threadId != null) {
      try {
        await postPeersonaEnd(chatbotId, threadId);
      } catch {
        /* best-effort */
      }
    }
    // Clear the restore pointer so the reload after an explicit reset starts
    // fresh rather than resuming the thread the user just ended.
    if (chatbotId != null) {
      writeLastThreadId(chatbotId, null);
      void queryClient.invalidateQueries({ queryKey: peersonaKeys.threads(chatbotId) });
    }
    clearThreadState();
  }, [cancelStream, chatbotId, threadId, clearThreadState, queryClient]);

  const value = useMemo<PeersonaContextValue>(
    () => ({
      chatbotId,
      chatbot: chatbot ?? null,
      panelOpen,
      openPanel,
      closePanel,
      togglePanel,
      setChatbotId,
      openPanelForChatbot,
      openPanelForChatbotThread,
      messages,
      threadId,
      isSending,
      stage,
      lastTier,
      lastChunks,
      recentThreads,
      isLoadingThread,
      sendMessage,
      startNewChat,
      loadThread,
      resetSession,
      cancelStream,
    }),
    [
      chatbotId,
      chatbot,
      panelOpen,
      openPanel,
      closePanel,
      togglePanel,
      setChatbotId,
      openPanelForChatbot,
      openPanelForChatbotThread,
      messages,
      threadId,
      isSending,
      stage,
      lastTier,
      lastChunks,
      recentThreads,
      isLoadingThread,
      sendMessage,
      startNewChat,
      loadThread,
      resetSession,
      cancelStream,
    ],
  );

  return <PeersonaContext.Provider value={value}>{children}</PeersonaContext.Provider>;
};
